<script setup lang="ts">
import { formatReviewCount } from '@/features/reviews';
import { type ContactFieldsFragment } from '@/gql/graphql';
import { makeFragmentData } from '@/gql';
import { contactFieldsFragment } from '@/features/users';
import PartnerContactsModal from './PartnerContactsModal.vue';
import { MapIcon } from '@glow/shared/components/icons';

export type PartnerHeaderPreviewPartner = {
  id: number;
  fullName: string;
  avatarUrl?: string | null;
  rating: number;
  reviewCount: number;
  address: string;
  hasMultipleWorkPlaces: boolean;
} & ContactFieldsFragment;

const props = withDefaults(
  defineProps<{
    partner: PartnerHeaderPreviewPartner;
    withMessageButton?: boolean;
    isMessageLoading?: boolean;
  }>(),
  {
    withMessageButton: true,
    isMessageLoading: false,
  }
);
const emit = defineEmits<{
  (e: 'message'): void;
}>();

const isContactsModalVisible = ref(false);
const contactFields = computed(() =>
  makeFragmentData(props.partner, contactFieldsFragment)
);
</script>

<template>
  <div class="header-preview">
    <div class="d-flex flex-nowrap gap-3 align-items-center w-100">
      <a href="#">
        <AppAvatar
          size="sm"
          :name="props.partner.fullName"
          :src="props.partner.avatarUrl"
        />
      </a>
      <div class="flex-grow-1">
        <a href="#" class="text-black">{{ props.partner.fullName }}</a>
        <div class="d-flex flex-nowrap align-items-center">
          <AppRating :model-value="props.partner.rating" />
          <span class="text-accent">
            ({{ formatReviewCount(props.partner.reviewCount) }})
          </span>
        </div>
      </div>
      <AppButton
        v-if="props.withMessageButton"
        class="d-none d-sm-block py-2"
        variant="outlined"
        :loading="props.isMessageLoading"
        @click="emit('message')"
      >
        Сообщение
      </AppButton>
      <AppButton
        class="py-2"
        variant="outlined"
        @click="isContactsModalVisible = true"
      >
        Контакты
      </AppButton>
    </div>
    <div class="d-none d-lg-flex flex-nowrap align-items-center gap-3">
      <AppIcon :icon="MapIcon" color="accent" />
      <div class="overflow-hidden">
        <address class="header-preview__address">
          {{ props.partner.address }}
        </address>
        <a v-if="props.partner.hasMultipleWorkPlaces" href="#work-places">
          Выбрать другое место
        </a>
      </div>
    </div>
    <PartnerContactsModal
      v-model="isContactsModalVisible"
      :fields="contactFields"
    />
  </div>
</template>

<style scoped lang="scss">
.header-preview {
  display: grid;
  grid-template-columns: 1fr minmax(0, 0.42fr);
  gap: 75px;

  @include media-breakpoint-down(lg) {
    display: flex;
  }

  @include media-breakpoint-down(md) {
    padding: 0 var(--app-spacing-4);
  }

  &__address {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
